/**
 * BotContextFiles Component
 * Displays and manages context files attached to a bot
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { UploadArea, FilePreviewModal } from '@/components/common';
import { KNOWLEDGE_ACCEPT } from '@/constants/knowledgeFileTypes';
import botService from '@modules/chatbots/services/botService';
import type { BotAttachment } from '@/types/models';
import { showNotification } from '@/utils/showNotification';
import './BotContextFiles.css';

interface BotContextFilesProps {
  botId: string;
  /**
   * Fires AFTER the auto-file-sync cascade has had time to run, so the
   * parent can refetch the bot row and refresh its `files_context_prompt`
   * display. Called ~8s after a successful upload or delete. Optional —
   * if absent, only the attachments list refreshes.
   */
  onContextChanged?: () => void;
  children?: React.ReactNode;
}

/** SVG file type icon component */
function FileTypeIcon({ fileType }: { fileType: string }) {
  const type = fileType?.toLowerCase() || '';

  if (type.includes('pdf')) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type.includes('word') || type.includes('doc') || type.includes('apple.pages')) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type.includes('csv') || type.includes('excel') || type.includes('spreadsheet') || type.includes('apple.numbers')) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="8" y="12" width="8" height="6" rx="1" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="12" y1="12" x2="12" y2="18" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="8" y1="15" x2="16" y2="15" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  // Default document icon
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BotContextFiles({ botId, onContextChanged, children }: BotContextFilesProps) {
  // After upload or delete the auto-file-sync cascade regenerates the
  // bot's files_context_prompt on a ~5s debounced timer. We poke the
  // parent at 3s, 5s, 8s, 12s, 18s, 25s — six firings spread across
  // the realistic regen window. The parent's onContextChanged callback
  // refetches the bot and the bundle list; the first poll that
  // returns a CHANGED value visually updates the inline
  // ContextPromptViewer without the user needing to close + reopen
  // the modal. Idempotent — if the value isn't ready yet the parent
  // refetch is cheap.
  const scheduleContextRefresh = useCallback(() => {
    if (!onContextChanged) return;
    const checkpoints = [3_000, 5_000, 8_000, 12_000, 18_000, 25_000];
    for (const ms of checkpoints) {
      setTimeout(() => onContextChanged(), ms);
    }
  }, [onContextChanged]);
  const { t } = useLanguage();
  const [attachments, setAttachments] = useState<BotAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<{ filePath?: string; fileId?: string; fileName: string; fileType: string } | null>(null);

  // Allowed types for "always in context" toggle
  const CONTEXT_ALLOWED_EXTENSIONS = ['.txt', '.md', '.csv', '.json', '.doc', '.docx', '.pdf', '.pages'];

  // Load attachments on mount
  useEffect(() => {
    loadAttachments();
  }, [botId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      const data = await botService.getBotAttachments(botId);
      setAttachments(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load attachments:', err);
      setError(t('bots.contextFiles.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Validate against the ONE canonical allowlist (KNOWLEDGE_ACCEPT — the
    // backend mirror). A stale local copy here once silently dropped whole
    // multi-file batches because it didn't know .xlsm/.pptx.
    const allowedTypes = KNOWLEDGE_ACCEPT.split(',');
    const invalidFiles = fileArray.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return !allowedTypes.includes(ext);
    });
    const validFiles = fileArray.filter(f => !invalidFiles.includes(f));

    if (invalidFiles.length > 0) {
      // Name the rejects, keep the rest — one bad pick must not cost the batch.
      showNotification(
        `${t('bots.contextFiles.unsupportedType')}: ${invalidFiles.map(f => f.name).join(', ')}`,
        'error'
      );
    }

    // Upload files one by one
    for (const file of validFiles) {
      try {
        setUploading(true);
        setUploadProgress(0);

        const attachment = await botService.uploadBotFile(
          botId,
          file,
          (percent) => setUploadProgress(percent)
        );

        setAttachments(prev => [...prev, attachment]);
        showNotification(
          t('bots.contextFiles.uploadSuccess', { fileName: file.name }),
          'success'
        );
        scheduleContextRefresh();
      } catch (err: any) {
        console.error('Upload failed:', err);
        const backendError = err.response?.data?.error;
        let userMessage: string;

        if (backendError) {
          // Check for specific error types and provide friendly messages
          if (backendError.toLowerCase().includes('encoding') ||
              backendError.toLowerCase().includes('decode') ||
              backendError.toLowerCase().includes('codec')) {
            userMessage = t('bots.contextFiles.fileEncodingError', { fileName: file.name });
          } else if (backendError.toLowerCase().includes('content does not match')) {
            // File type mismatch (e.g., txt renamed to pdf)
            userMessage = t('bots.contextFiles.fileTypeMismatch', { fileName: file.name });
          } else if (backendError.toLowerCase().includes('validation failed')) {
            userMessage = t('bots.contextFiles.fileValidationError', { fileName: file.name });
          } else if (backendError.toLowerCase().includes('already attached')) {
            userMessage = backendError; // Already clear message from backend
          } else {
            // Show the backend error directly for other cases
            userMessage = t('bots.contextFiles.fileProcessingFailed', {
              fileName: file.name,
              error: backendError
            });
          }
        } else {
          userMessage = t('bots.contextFiles.uploadError');
        }

        showNotification(userMessage, 'error');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleDelete = async (attachmentId: string, fileName: string) => {
    if (!confirm(t('bots.contextFiles.deleteConfirm', { fileName }))) {
      return;
    }

    try {
      await botService.deleteBotFile(botId, attachmentId);
      setAttachments(prev => prev.filter(a => a.bot_attachment_id !== attachmentId));
      showNotification(t('bots.contextFiles.deleteSuccess'), 'success');
      scheduleContextRefresh();
    } catch (err: any) {
      console.error('Delete failed:', err);
      showNotification(t('bots.contextFiles.deleteError'), 'error');
    }
  };

  const isContextAllowed = (fileType: string, fileName: string): boolean => {
    const ext = '.' + (fileName || '').split('.').pop()?.toLowerCase();
    const mime = (fileType || '').toLowerCase();
    if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) return false;
    return CONTEXT_ALLOWED_EXTENSIONS.includes(ext);
  };

  const handleToggleContext = async (attachment: BotAttachment) => {
    const newValue = !attachment.include_as_context;
    const id = attachment.bot_attachment_id;

    setTogglingIds(prev => new Set(prev).add(id));
    try {
      const updated = await botService.updateBotAttachment(botId, id, { include_as_context: newValue });
      setAttachments(prev => prev.map(a => a.bot_attachment_id === id ? { ...a, include_as_context: updated.include_as_context } : a));
      showNotification(t('bots.contextFiles.contextToggleSuccess'), 'success');
      scheduleContextRefresh();
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('bots.contextFiles.contextToggleError');
      setError(msg);
      setTimeout(() => setError(null), 6000);
    } finally {
      setTogglingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [botId]);

  // showNotification imported at top of file

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div dir="auto" className="bot-context-files">
      <div className="data-bundle-section">
        <label className="section-label">{t('bots.contextFiles.title')}</label>
        <p className="section-description">
          {t('bots.contextFiles.description')}
        </p>

        {/* Why a file can appear in both lists. It is a real duplicate of
            bookkeeping, not of the file — the Library link and the older
            attachment are two records of the same document, and both are kept
            on purpose while the change can still be undone. Saying so is
            cheaper than leaving people to wonder whether they broke it. */}
        <p className="section-note">{t('bots.contextFiles.duringTheMove')}</p>

        {/* Error Message */}
        {error && <div className="context-files-error">{error}</div>}

        {/* Files List */}
        {!loading && attachments.length > 0 && (
          <div className="bundle-files-list">
            {attachments.map((attachment) => (
              <div key={attachment.bot_attachment_id} className="bundle-file-item">
                <div
                  className="bundle-file-clickable"
                  onClick={() => {
                    if (attachment.file?.file_id || attachment.file?.file_path) {
                      setPreviewFile({
                        fileId: attachment.file.file_id,
                        filePath: attachment.file.file_path,
                        fileName: attachment.file.file_name || 'Unknown',
                        fileType: attachment.file.file_type || '',
                      });
                    }
                  }}
                  title={t('filePreview.preview')}
                >
                <div className="bundle-file-icon">
                  <FileTypeIcon fileType={attachment.file?.file_type || ''} />
                </div>
                <div className="bundle-file-info">
                  <span dir="auto" className="bundle-file-name">
                    {attachment.file?.file_name || 'Unknown'}
                    {(attachment.file as any)?.source_url && (
                      <a
                        href={(attachment.file as any).source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-flex', marginInlineStart: '6px', color: '#3b82f6', opacity: 0.6 }}
                        title={(attachment.file as any).source_url}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    )}
                  </span>
                  <span className="bundle-file-meta">
                    <span className="bundle-file-size">
                      {formatFileSize(attachment.file?.file_size_bytes || 0)}
                    </span>
                  </span>
                </div>
                </div>
                <div className="file-context-toggle" title={
                  isContextAllowed(attachment.file?.file_type || '', attachment.file?.file_name || '')
                    ? t('bots.contextFiles.alwaysInContextTooltip')
                    : t('bots.contextFiles.contextToggleUnsupportedType')
                }>
                  <span className="context-toggle-label">{t('bots.contextFiles.alwaysInContext')}</span>
                  <label className="context-switch">
                    <input
                      type="checkbox"
                      checked={attachment.include_as_context || false}
                      onChange={() => handleToggleContext(attachment)}
                      disabled={
                        !isContextAllowed(attachment.file?.file_type || '', attachment.file?.file_name || '') ||
                        togglingIds.has(attachment.bot_attachment_id) ||
                        attachment.context_mode === 'pending' ||
                        attachment.context_mode === 'error'
                      }
                    />
                    <span className="context-switch-slider"></span>
                  </label>
                </div>
                <button
                  className="bundle-file-delete"
                  onClick={() => handleDelete(attachment.bot_attachment_id, attachment.file?.file_name || '')}
                  title={t('bots.contextFiles.delete')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div className="files-loading">
            <div className="spinner"></div>
            <span>{t('bots.contextFiles.loading')}</span>
          </div>
        )}

        {/* Upload Zone */}
        <UploadArea
          uploading={uploading}
          uploadProgress={uploadProgress}
          dragOver={dragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileSelect={(files) => handleFileUpload(files)}
          accept={KNOWLEDGE_ACCEPT}
          disabled={uploading}
        />

        {children}
      </div>

      <FilePreviewModal
        visible={!!previewFile}
        filePath={previewFile?.filePath}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
